import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useToast } from '../../context/ToastContext';
import { ROUTES } from '../../constants/routes';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import transparentLogo from '../../assets/images/Transparent_logo.png';

// signInWithEmailAndPassword returns 'auth/invalid-credential' for both wrong
// password and unknown email in Firebase JS SDK 10.x (no user enumeration),
// so all three map to one generic message.
const mapAuthError = (code) => {
  switch (code) {
    case 'auth/email-already-in-use': return 'An account with this email already exists.';
    case 'auth/invalid-credential':
    case 'auth/wrong-password':
    case 'auth/user-not-found':      return 'Incorrect email or password.';
    case 'auth/weak-password':       return 'Password must be at least 6 characters.';
    case 'auth/too-many-requests':   return 'Too many attempts. Please try again later.';
    case 'auth/invalid-email':       return 'Please enter a valid email address.';
    default:                         return 'Something went wrong. Please try again.';
  }
};

const INITIAL_FORM = { displayName: '', email: '', password: '', confirmPassword: '' };

const Login = () => {
  const { user, signIn, signInWithEmail, signUpWithEmail, resetPassword } = useAuth();
  const { showToast } = useToast();
  const navigate = useNavigate();

  const [mode, setMode] = useState('signin'); // 'signin' | 'signup' | 'forgot'
  const [form, setForm] = useState(INITIAL_FORM);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (user) navigate(ROUTES.GAMES.PLAY);
  }, [user, navigate]);

  const set = (field) => (e) => setForm((p) => ({ ...p, [field]: e.target.value }));

  const switchMode = (next) => {
    setMode(next);
    setForm(INITIAL_FORM);
    setError('');
  };

  const handleGoogleSignIn = async () => {
    setError('');
    setGoogleLoading(true);
    try {
      await signIn();
      navigate(ROUTES.GAMES.PLAY);
    } catch (err) {
      setError(mapAuthError(err.code));
      console.error(err);
    } finally {
      setGoogleLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (mode === 'signup') {
      if (form.password.length < 6) { setError('Password must be at least 6 characters.'); return; }
      if (form.password !== form.confirmPassword) { setError('Passwords do not match.'); return; }
    }

    setLoading(true);
    try {
      if (mode === 'signin') {
        await signInWithEmail(form.email, form.password);
        navigate(ROUTES.GAMES.PLAY);
      } else if (mode === 'signup') {
        await signUpWithEmail(form.email, form.password, form.displayName);
        navigate(ROUTES.GAMES.PLAY);
      } else {
        await resetPassword(form.email);
        showToast('Password reset email sent.', 'success');
        switchMode('signin');
      }
    } catch (err) {
      setError(mapAuthError(err.code));
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-page">
      <div className="login-card">
        <div className="login-card__logo" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
          <img src={transparentLogo} alt="Bible Contest Logo" className="login-card-logo-img" />
          <span>Bible Contest</span>
        </div>
        <p className="login-card__subtitle">Sign in to play Scripture games, track rankings, and host leagues</p>

        <Button
          variant="ghost"
          className="login-card__btn"
          onClick={handleGoogleSignIn}
          loading={googleLoading}
          icon={
            <svg width="20" height="20" viewBox="0 0 48 48">
              <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
              <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
              <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
              <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.18 1.48-4.97 2.36-8.16 2.36-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
            </svg>
          }
        >
          Continue with Google
        </Button>

        <div className="login-card__divider"><span>or</span></div>

        <form onSubmit={handleSubmit}>
          {mode === 'signup' && (
            <Input label="Display Name" value={form.displayName} onChange={set('displayName')} required />
          )}
          <Input label="Email" type="email" value={form.email} onChange={set('email')} required />
          {mode !== 'forgot' && (
            <Input label="Password" type="password" value={form.password} onChange={set('password')} required />
          )}
          {mode === 'signup' && (
            <Input label="Confirm Password" type="password" value={form.confirmPassword} onChange={set('confirmPassword')} required />
          )}

          {error && <p className="login-card__error">{error}</p>}

          <Button type="submit" className="login-card__btn" loading={loading}>
            {mode === 'signin' ? 'Sign In' : mode === 'signup' ? 'Sign Up' : 'Send Reset Email'}
          </Button>
        </form>

        <div className="login-card__links">
          {mode === 'signin' && (
            <>
              <button type="button" className="login-card__link" onClick={() => switchMode('forgot')}>Forgot password?</button>
              <button type="button" className="login-card__link" onClick={() => switchMode('signup')}>{"Don't have an account? Sign up"}</button>
            </>
          )}
          {mode === 'signup' && (
            <button type="button" className="login-card__link" onClick={() => switchMode('signin')}>Already have an account? Sign in</button>
          )}
          {mode === 'forgot' && (
            <button type="button" className="login-card__link" onClick={() => switchMode('signin')}>Back to sign in</button>
          )}
        </div>

        <div className="login-legal-notice">
          By continuing, you agree to Bible Contest&apos;s{' '}
          <Link to={ROUTES.LEGAL.TERMS} className="login-legal-link" target="_blank" rel="noopener noreferrer">
            Terms &amp; Conditions
          </Link>{' '}
          and acknowledge our{' '}
          <Link to={ROUTES.LEGAL.PRIVACY} className="login-legal-link" target="_blank" rel="noopener noreferrer">
            Privacy Policy
          </Link>.
        </div>
      </div>
    </div>
  );
};

export default Login;
